import React, { useContext, useEffect, useMemo } from 'react';
import { AzureMapsContext } from 'react-azure-maps';
import { BusPositionHistoryContext } from '../../bushistory/BusHistoryContext';
import { CameraBounds, normalizeCameraBounds } from './AzureMapCameraBounds';

type CameraPadding = number | {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
};

const DEFAULT_CAMERA_PADDING: CameraPadding = {
    top: 60,
    bottom: 60,
    left: 60,
    right: 60,
};

interface AzureMapCameraControlProps {
    bounds?: CameraBounds | any | null;
    padding?: CameraPadding;
    maxZoom?: number;
    disabled?: boolean;
}

export const AzureMapCameraControl: React.FC<AzureMapCameraControlProps> = ({ bounds, padding, maxZoom = 18, disabled = false }) => {
    const { mapRef, isMapReady } = useContext(AzureMapsContext);

    // BusPositionHistoryContext may be unavailable when this control is used outside BusHistory pages.
    // Guard against undefined to prevent destructuring errors.
    const busHistoryCtx = useContext(BusPositionHistoryContext as any) as any | undefined;
    const mapsBounds = busHistoryCtx?.mapsBounds;

    const contextBounds = useMemo(() => normalizeCameraBounds(mapsBounds), [mapsBounds]);
    const inputBounds = useMemo(() => normalizeCameraBounds(bounds), [bounds]);
    const effectiveBounds = inputBounds ?? contextBounds;
    const cameraPadding = padding ?? DEFAULT_CAMERA_PADDING;

    useEffect(() => {
        if (disabled) {
            return;
        }
        if (!(isMapReady && mapRef)) {
            return;
        }
        if (effectiveBounds) {
            mapRef.setCamera({
                bounds: effectiveBounds as any,
                padding: cameraPadding,
                duration: 500,
                maxZoom,
            });
            return;
        }
        if (mapsBounds?.center) {
            const position = [mapsBounds.center.longitude, mapsBounds.center.latitude];
            mapRef.setCamera({
                center: position,
                duration: 1500,
            });
        }
    }, [disabled, isMapReady, mapRef, effectiveBounds, cameraPadding, maxZoom, mapsBounds]);
    if (disabled) {
        return null;
    }
    return null;
};
